import React, { useEffect } from "react";
import { reduxForm } from "redux-form";
import {
  isAmountPastDueBetweenBalanceAndPayoff,
  removeNonNumericCharacters,
} from "../../../utils/utils";
import { AuthContinue } from "../misc/auth-continue.component";
import AuthAchFields from "./field-components/general/auth-ach-fields.component";
import { AuthAmountField } from "./field-components/general/auth-amount-field.component";
import { AuthPaymentContainer } from "../misc/auth-payment-container.component";
import { AuthRestartButton } from "../misc/restart-button.component";
import {
  determinePayoffInfoClosedEnd,
  determinePayoffInfoOpenEnd,
  determinePayoffInfoEscrow,
  ninetyDaysOverdueMessage,
  isExcessPayment,
  principalOnlyMessage,
  determineOneTimePaymentsByline,
  generateLoanDescription,
  payoffAmountExceedsAchLimit,
} from "../misc/messaging-utils";
import { formatOlbStyleAuthAmountValueFromBackend } from "../misc/auth-amount-utility";
import DatePicker from "react-datepicker";
import { AuthAchScheduledPaymentDatePicker } from "./field-components/general/auth-ach-scheduled-payment-datepicker.component";
import Show from "../../../utils/show.component";
import moment from "moment";

const AuthPaymentExternalAchForm = (props) => {
  const hasSyncErrors = props.thisForm && props.thisForm.syncErrors;
  const amount = props.thisForm && props.thisForm.values && props.thisForm.values.Amount;
  const scheduledPaymentDate =
    props.thisForm && props.thisForm.values && props.thisForm.values.ScheduledPaymentDate;

  const isPayoff = props.hasSelectedPayoff || props.amountPastDueBetweenPayoffAndBalance;
  const hasEscrow = props.selectedLoan.HasEscrowBalance;

  const shouldShowStandardMessaging = () => {
    return !props.isPrincipalOnly && !props.hasSelectedPayoff;
  };

  let payoffObject = {};

  if (!props.isPrincipalOnly) {
    if (hasEscrow) {
      payoffObject = determinePayoffInfoEscrow(amount, props.PayoffAmount);
    } else {
      payoffObject = props.selectedLoan.IsOpenEndedLoan
        ? determinePayoffInfoOpenEnd(amount, props.PayoffAmount, props.PrincipalBalance)
        : determinePayoffInfoClosedEnd(
            amount,
            props.PrincipalBalance,
            props.TenDayPayoffAmount,
            props.amountPastDueBetweenPayoffAndBalance,
          );
    }
  }

  const getIsAmountOverCurrentBalance = () => {
    const stringAmount = amount ? removeNonNumericCharacters(amount) : 0;
    const numericAmount = parseFloat(stringAmount);

    return numericAmount > props.selectedLoan.CurrentBalance;
  };

  const excessMessage = isExcessPayment(
    amount,
    props.selectedLoan.AmountDue,
    props.PrincipalBalance,
  );

  const amountIsPastDueIsBetweenBalanceAndPayoff = isAmountPastDueBetweenBalanceAndPayoff(
    props.selectedLoan.AmountPastDue,
    props.PayoffAmount,
    props.selectedLoan.CurrentBalance,
  );

  const bylinePastDue = amountIsPastDueIsBetweenBalanceAndPayoff
    ? props.TenDayPayoffAmount
    : props.selectedLoan.AmountPastDue;

  const byline = determineOneTimePaymentsByline(
    props.hasSelectedPayoff,
    bylinePastDue,
    props.TenDayPayoffAmount,
    props.selectedLoan.RegularPaymentAmount,
  );

  useEffect(() => {
    props.change("NameOnAccount", props.authPayment.memberInfo.memberName);
  }, []);

  useEffect(() => {
    if (isPayoff) {
      props.change("Amount", formatOlbStyleAuthAmountValueFromBackend(props.TenDayPayoffAmount));
      props.touch("Amount");
    } else if (props.selectedLoan.AmountPastDue) {
      props.change(
        "Amount",
        formatOlbStyleAuthAmountValueFromBackend(props.selectedLoan.AmountPastDue),
      );
    }
  }, []);

  // On submission, send a call to the payoff check endpoint
  const submitForm = () => {
    window.scrollTo(0, 0);
    let paymentDetails = Object.assign({}, props.thisForm.values);
    paymentDetails.Amount = removeNonNumericCharacters(paymentDetails.Amount);
    paymentDetails.IsPrincipalOnly = props.isPrincipalOnly;

    props.callAuthCheckPayoff(paymentDetails, "ach");
  };

  // If the call to payoff check has determined this payment is allowed,
  // send the next request
  useEffect(() => {
    if (props.authPayment.payoffCheck.PaymentIsAllowed) {
      let paymentDetails = Object.assign({}, props.thisForm.values);

      props.callAuthCalcPostDate(paymentDetails);
    }
  }, [props.authPayment.payoffCheck]);

  useEffect(() => {
    if (props.achPostDate) {
      props.setProcessStep(2);
    }
  }, [props.achPostDate]);

  const datePickerDisabled =
    (payoffObject && payoffObject.greaterOrEqualToPayoff) || getIsAmountOverCurrentBalance();

  return (
    <AuthPaymentContainer
      title="Payment Details"
      topChildren={[
        ninetyDaysOverdueMessage(
          props.selectedLoan.AmountPastDue,
          props.selectedLoan.Is90DaysOverdue,
          isPayoff,
        ),
        principalOnlyMessage(props.isPrincipalOnly),
        payoffAmountExceedsAchLimit(
          props.hasSelectedPayoff,
          props.TenDayPayoffAmount,
          props.selectedLoan.MaxAllowedAchAmount,
        ),
      ]}
    >
      <div className="bold f20">{generateLoanDescription(props.selectedLoan)}</div>
      <div className="f14">{byline}</div>
      <div className="my-4 divider"></div>
      <form onSubmit={props.handleSubmit(submitForm)}>
        <AuthAmountField
          isRecurring={false}
          isDebit={false}
          change={props.change}
          isAch={true}
          isPrincipalOnly={props.isPrincipalOnly}
          hasSelectedPayoff={isPayoff}
          currentBalance={props.selectedLoan.CurrentBalance}
          isOpenEnd={props.selectedLoan.IsOpenEndedLoan}
          payoffAmount={props.PayoffAmount}
          maxAllowedAchAmount={props.selectedLoan.MaxAllowedAchAmount}
          disabled={props.selectedLoan.Is90DaysOverdue || props.hasSelectedPayoff}
        />
        <Show condition={!props.isPrincipalOnly && !isPayoff}>
          <AuthAchScheduledPaymentDatePicker
            scheduledPaymentDate={scheduledPaymentDate}
            change={props.change}
            disabled={datePickerDisabled}
          />
        </Show>
        {/* Only show these messages for no-principal only payments */}
        {shouldShowStandardMessaging() && excessMessage}
        {shouldShowStandardMessaging() && payoffObject.component}
        <AuthAchFields
          thisForm={props.thisForm}
          achInstitution={props.achInstitution}
          change={props.change}
          disabled={props.selectedLoan.Is90DaysOverdue}
        />
        <AuthContinue
          title="Continue"
          type="submit"
          disabled={
            props.submitting ||
            !props.achInstitution.achInstitution ||
            hasSyncErrors ||
            payoffObject.disabled
          }
        />
        <AuthRestartButton backToLoans={props.backToLoans} />
      </form>
    </AuthPaymentContainer>
  );
};

//Connect to Redux store
export const AuthPaymentExternalAch = reduxForm({
  form: "AuthPaymentExternalAch",
  destroyOnUnmount: false,
  initialValues: {
    Amount: "",
    ScheduledPaymentDate: moment().toDate(),
    isChecking: "true",
    NameOnAccount: "",
    RoutingNumber: "",
    AccountNumber: "",
    AccountNumberVerification: "",
  },
})(AuthPaymentExternalAchForm);



// WEBPACK FOOTER //
// ./src/components/authenticated-payments/payment-forms/auth-payment-external-ach.component.js